import React from 'react';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Volume2, BookOpenCheck, Sparkles, ArrowRight, Calendar, Heart, GraduationCap, Languages, Globe2 } from 'lucide-react';

const Landing = () => {
  const { isKidsMode, toggleMode } = useMode();
  const { lang, toggleLang } = useLang();
  const navigate = useNavigate();

  const accent = isKidsMode ? 'amber' : 'teal';
  const accentSolid = isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800';
  const accentLight = isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-50 text-teal-800';
  const accentText = isKidsMode ? 'text-amber-700' : 'text-teal-800';
  const accentBorder = isKidsMode ? 'border-amber-300' : 'border-teal-300';

  const tr = lang === 'tr';

  return (
    <div
      className="min-h-screen"
      style={{
        background: isKidsMode
          ? 'radial-gradient(circle at 15% 0%, #fef3c7 0%, transparent 38%), radial-gradient(circle at 85% 100%, #fed7aa 0%, transparent 38%), #fafaf9'
          : 'radial-gradient(circle at 15% 0%, #ccfbf1 0%, transparent 38%), radial-gradient(circle at 85% 100%, #99f6e4 0%, transparent 38%), #fafaf9',
      }}
      data-testid="landing-page"
    >
      {/* Header */}
      <header className="px-5 sm:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className={`w-10 h-10 rounded-xl ${accentSolid} flex items-center justify-center shadow-md`}>
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Hafızım
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Language selector hidden — Turkish only */}
          <button
            type="button"
            onClick={toggleMode}
            data-testid="landing-mode-toggle"
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${isKidsMode ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-teal-100 border-teal-300 text-teal-800'}`}
          >
            {isKidsMode ? '🧒 ' + (tr ? 'Çocuk' : 'Kids') : '👤 ' + (tr ? 'Yetişkin' : 'Adult')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/auth')}
            data-testid="login-button"
            className={`hidden sm:inline-flex px-4 py-2 rounded-xl text-sm font-semibold text-white ${accentSolid}`}
          >
            {tr ? 'Giriş Yap' : 'Sign In'}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="px-5 sm:px-10 pt-6 sm:pt-14 pb-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${accentLight} border ${accentBorder} mb-6`}>
              <Sparkles className="w-3.5 h-3.5" />
              {tr ? 'Diyanet kaynaklı içerikle' : 'Powered by Diyanet sources'}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight leading-[1.1]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isKidsMode
                ? (tr ? 'Kur\'an\'a oynayarak başla.' : 'Start the Quran by playing.')
                : (tr ? 'Kur\'an yolculuğun, kaldığın yerden.' : 'Your Quran journey, from where you paused.')}
            </h1>
            <p className="text-lg text-stone-600 mt-5 leading-relaxed max-w-xl">
              {isKidsMode
                ? (tr ? 'Elif-Ba\'dan kısa surelere — sesli oyunlar, rozetler ve eğlenceli alıştırmalarla.' : 'From Elif-Ba to short surahs — with sound games, badges, and fun exercises.')
                : (tr ? 'Hatim takibi, sayfa modunda Mushaf okuma, 4 farklı kâriden dinle, özel günlerde ayetler.' : 'Hatim tracking, Mushaf page mode, 4 reciters, verses for holy days.')}
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                data-testid="get-started-button"
                className={`inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-base font-semibold text-white shadow-lg ${accentSolid} transition-transform hover:-translate-y-0.5`}
              >
                {tr ? 'Ücretsiz Başla' : 'Start Free'}
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('features');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="learn-more-button"
                className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl text-base font-semibold text-stone-700 bg-white border border-stone-200 hover:border-stone-300"
              >
                {tr ? 'Neler Var?' : 'What\'s Inside?'}
              </button>
            </div>

            {/* Trust strip */}
            <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-stone-500">
              <div className="flex items-center gap-1.5">
                <Languages className="w-4 h-4" /> {tr ? 'TR + EN destek' : 'TR + EN support'}
              </div>
              <div className="flex items-center gap-1.5">
                <Volume2 className="w-4 h-4" /> {tr ? '4 farklı kâri' : '4 reciters'}
              </div>
              <div className="flex items-center gap-1.5">
                <BookOpenCheck className="w-4 h-4" /> {tr ? '604 Mushaf sayfası' : '604 Mushaf pages'}
              </div>
            </div>
          </div>

          {/* Visual: Stacked feature cards */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-6 transform rotate-[-2deg] hover:rotate-0 transition-transform">
                <div className={`w-12 h-12 rounded-2xl ${accentLight} flex items-center justify-center mb-3`}>
                  <BookOpen className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {tr ? 'Sayfa Modu' : 'Page Mode'}
                </p>
                <p className="text-xs text-stone-500 mt-1">{tr ? 'Mushaf görünümünde oku' : 'Read in Mushaf layout'}</p>
                <p className="mt-4 text-2xl text-right text-stone-800" style={{ fontFamily: 'Amiri, serif', direction: 'rtl', lineHeight: 1.6 }}>
                  بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ
                </p>
              </div>
              <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-6 transform rotate-[3deg] hover:rotate-0 transition-transform mt-8">
                <div className={`w-12 h-12 rounded-2xl ${accentLight} flex items-center justify-center mb-3`}>
                  <GraduationCap className="w-6 h-6" />
                </div>
                <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Elif-Ba
                </p>
                <p className="text-xs text-stone-500 mt-1">{tr ? '28 harf + sesler' : '28 letters + sounds'}</p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {['ا','ب','ت','ث','ج','ح'].map((l) => (
                    <span key={l} className="w-7 h-7 rounded-lg bg-stone-100 flex items-center justify-center text-base text-stone-700" style={{ fontFamily: 'Amiri, serif' }}>{l}</span>
                  ))}
                  <span className="w-7 h-7 rounded-lg bg-stone-100 flex items-center justify-center text-xs text-stone-500">+22</span>
                </div>
              </div>
              <div className="bg-white rounded-3xl border border-stone-200 shadow-xl p-6 transform rotate-[2deg] hover:rotate-0 transition-transform col-span-2">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl ${accentLight} flex items-center justify-center flex-shrink-0`}>
                    <Calendar className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {tr ? 'Özel Günler & Kandiller' : 'Holy Nights & Eids'}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5">{tr ? 'Tıkla, o güne özel ayetler, hadis ve dualar' : 'Click for verses, hadiths, prayers for each day'}</p>
                  </div>
                  <div className="text-right text-xs text-stone-500">
                    <div className="font-semibold text-stone-800">10+</div>
                    <div>{tr ? 'gün' : 'days'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section id="features" className="px-5 sm:px-10 py-16 max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className={`text-xs uppercase tracking-[0.25em] ${accentText} font-semibold mb-2`}>
            {tr ? 'Neler Var?' : 'Features'}
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {tr ? 'Tek bir yerde, eksiksiz bir Kur\'an deneyimi' : 'A complete Quran experience, in one place'}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: BookOpen, t_tr: 'Kur\'an Oku', t_en: 'Read Quran', d_tr: 'Sayfa modu (Mushaf) ve Ayet modu. Türkçe meal + font zoom.', d_en: 'Page mode (Mushaf) and Ayah mode. English translation + font zoom.' },
            { icon: Volume2, t_tr: 'Kur\'an Dinle', t_en: 'Listen Quran', d_tr: '4 kâri: Alafasy, Sudais, Abdul Basit, Husary.', d_en: '4 reciters: Alafasy, Sudais, Abdul Basit, Husary.' },
            { icon: BookOpenCheck, t_tr: 'Hatim Takibi', t_en: 'Hatim Tracker', d_tr: 'Sure & sayfa bazlı ilerleme. Kaldığın yerden devam.', d_en: 'Surah & page based progress. Resume where you left.' },
            { icon: GraduationCap, t_tr: 'Elif-Ba (Çocuk)', t_en: 'Elif-Ba (Kids)', d_tr: 'Diyanet ses kayıtlarıyla 28 harf. Sesli quiz oyunu.', d_en: 'Diyanet recordings for 28 letters. Sound quiz game.' },
            { icon: Calendar, t_tr: 'Özel Günler', t_en: 'Holy Days', d_tr: 'Mevlid, Kandiller, Bayramlar. Ayet, hadis, dua detayı.', d_en: 'Mawlid, holy nights, eids. Verses, hadiths, prayers.' },
            { icon: Heart, t_tr: 'Hadis & Dua', t_en: 'Hadiths & Prayers', d_tr: 'Sahih hadisler, namaz duaları, sabah/akşam zikirleri.', d_en: 'Sahih hadiths, prayer duas, morning/evening dhikr.' },
          ].map((f, idx) => {
            const Icon = f.icon;
            return (
              <div key={idx} className="p-6 bg-white rounded-2xl border border-stone-200 hover:border-stone-300 hover:shadow-md transition-all" data-testid={`feature-${idx + 1}`}>
                <div className={`w-12 h-12 rounded-2xl ${accentLight} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {tr ? f.t_tr : f.t_en}
                </h3>
                <p className="text-sm text-stone-500 leading-relaxed">{tr ? f.d_tr : f.d_en}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="px-5 sm:px-10 pb-16 max-w-7xl mx-auto">
        <div className={`rounded-3xl border ${accentBorder} ${accentLight} p-10 sm:p-14 text-center`}>
          <h3 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {tr ? 'Hazır mısın? Hadi başlayalım.' : 'Ready? Let\'s start.'}
          </h3>
          <p className="text-stone-700 mt-3 max-w-xl mx-auto">
            {tr ? 'Ücretsiz hesap oluştur, her gün biraz daha ilerle. Niyet yeter.' : 'Create a free account, progress a little each day. Intention is enough.'}
          </p>
          <button
            type="button"
            onClick={() => navigate('/auth')}
            data-testid="bottom-cta-button"
            className={`mt-6 inline-flex items-center gap-2 px-7 py-4 rounded-2xl text-base font-semibold text-white shadow-lg ${accentSolid}`}
          >
            {tr ? 'Aramıza Katıl' : 'Join Us'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="px-5 sm:px-10 py-8 text-center text-xs text-stone-400 space-y-2">
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a href="/gizlilik-politikasi" className="hover:text-stone-700 hover:underline" data-testid="landing-footer-privacy">Gizlilik & KVKK</a>
          <span>·</span>
          <a href="/kullanim-kosullari" className="hover:text-stone-700 hover:underline" data-testid="landing-footer-terms">Kullanım Koşulları</a>
          <span>·</span>
          <a href="/cerez-politikasi" className="hover:text-stone-700 hover:underline" data-testid="landing-footer-cookies">Çerez Politikası</a>
          <span>·</span>
          <a href="/iletisim" className="hover:text-stone-700 hover:underline" data-testid="landing-footer-contact">İletişim</a>
        </div>
        <div>© 2026 Hafızım · {tr ? 'Tüm hakları saklıdır' : 'All rights reserved'} · hafizim.com</div>
      </footer>
    </div>
  );
};

export default Landing;
